import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import { Typography } from '../../../display/Typography';
import { MainButton } from '../../../inputs/buttons/MainButton';

// Icons
import AlertIcon from '../../../../assets/icons/alert-icon.svg';

// Styled
import { Container, IconImg, TextGroup } from './styles';

// Types
import { GpsRequiredModalProps } from './types';

/**
 * V4 P14 — Modal bloqueante que pide reactivar el GPS durante un tramo activo.
 *
 * Características (FR-14):
 * - Sin X ni Cancel·lar: no es cerrable por el usuario.
 * - Click-away y Escape están desactivados en SuperModal para `GPS_REQUIRED`.
 * - Auto-dismiss: el consumidor cierra el modal cuando el navegador devuelve
 *   GPS válido con precisión alta (via `closeModal()` del reducer en su
 *   propio `useEffect`).
 * - Botón "Activar GPS" dispara `onRequestGps` del consumidor (que debe
 *   invocar `navigator.geolocation.getCurrentPosition` para re-solicitar permisos).
 */
export const GpsRequiredModal = ({
  data,
}: GpsRequiredModalProps): JSX.Element => {
  const { t } = useTranslation(['modals']);
  // Fix 2026-07-20 (QA usuario: "Aceptar no hace nada"): feedback local —
  // loading mientras se resuelve el intento y, si el permiso está BLOQUEADO
  // en el navegador (no re-prompta, MDN), instrucciones para reactivarlo.
  const [isRequesting, setIsRequesting] = useState(false);
  const [denied, setDenied] = useState(false);
  const isLoading = (data.isLoading ?? false) || isRequesting;

  const handleClick = useCallback(async () => {
    setIsRequesting(true);
    try {
      const result = await data.onRequestGps();
      setDenied(result === 'denied');
    } finally {
      setIsRequesting(false);
    }
  }, [data]);

  return (
    <Container>
      <IconImg src={AlertIcon} alt="" />
      <TextGroup>
        <Typography size="L" variant="semiBold">
          {t('modals:gpsRequired.title')}
        </Typography>
        <Typography size="M">{t('modals:gpsRequired.message')}</Typography>
        {denied && (
          <Typography size="S" colorText="garnet">
            {t('modals:gpsRequired.deniedHelp')}
          </Typography>
        )}
      </TextGroup>
      <MainButton
        text={t('modals:gpsRequired.enableButton')}
        onClick={handleClick}
        disabled={isLoading}
      />
    </Container>
  );
};

export default GpsRequiredModal;
